// src/pages/SupervisorPortal.js
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";

// EMBEDS
import SupervisorDashboard from "./SupervisorDashboard";
import PropertiesClients from "./PropertiesClients";
import Management from "./Management";
import SendInvoices from "./SendInvoices";
import ApprovedInvoices from "./ApprovedInvoices";
import LicenseCertificates from "./LicenseCertificates";
import TimeOffAdmin from "./TimeOffAdmin";
import Contracts from "./Contracts";
import Accounting from "./Accounting";

// OPERATIONS
import Vehicles from "./Vehicles";
import Vendors from "./Vendors";
import Contractors from "./Contractors";
import Supplies from "./Supplies";
import Reports from "./Reports";
import Notebook from "./Notebook";

// SETTINGS
import Staff from "./Staff";
import StaffSettings from "./StaffSettings";
import DataBase from "./DataBase";
import ProposalsPPDDropdownsAdmin from "./ProposalsPPDDropdownsAdmin";
import InvoiceDropdownsAdmin from "./InvoiceDropdownsAdmin";
import NotificationRecipients from "./NotificationRecipients";

// ADMIN
import AdminPortal from "./AdminPortal";
import AdminAccess from "./AdminAccess";

const SupervisorPortal = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // The open tab and the open More-menu section are remembered in
  // sessionStorage (same pattern as Home.js / JobHistory.js) so a browser
  // refresh lands back on the same embedded page. Unknown/stale values fall
  // back to the defaults (dashboard tab, no More section).
  const SUPERVISOR_ACTIVE_TAB_STORAGE_KEY = "priority_supervisor_active_tab";
  const SUPERVISOR_MORE_SECTION_STORAGE_KEY = "priority_supervisor_more_section";

  const VALID_SUPERVISOR_TABS = [
    "dashboard",
    "properties",
    "management",
    "invoices",
    "approved",
    "licenses",
    "timeoff",
    "contracts",
    "accounting",
  ];

  const VALID_SUPERVISOR_MORE_SECTIONS = [
    "vehicles",
    "vendors",
    "contractors",
    "supplies",
    "reports",
    "notebook",
    "staff",
    "staffsettings",
    "database",
    "ppddd",
    "invoicedd",
    "notificationrecipients",
    "adminportal",
    "adminaccess",
  ];

  // top hub tabs (main content tabs)
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const stored = sessionStorage.getItem(SUPERVISOR_ACTIVE_TAB_STORAGE_KEY);
      return VALID_SUPERVISOR_TABS.includes(stored) ? stored : "dashboard";
    } catch (e) {
      return "dashboard";
    }
  });

  // more menu popup
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // which embed to show from More menu (Settings/Admin items)
  const [moreSection, setMoreSection] = useState(() => {
    try {
      const stored = sessionStorage.getItem(SUPERVISOR_MORE_SECTION_STORAGE_KEY);
      return VALID_SUPERVISOR_MORE_SECTIONS.includes(stored) ? stored : null;
    } catch (e) {
      return null;
    }
  });

  useEffect(() => {
    try {
      if (activeTab) {
        sessionStorage.setItem(SUPERVISOR_ACTIVE_TAB_STORAGE_KEY, activeTab);
      }
      if (moreSection) {
        sessionStorage.setItem(SUPERVISOR_MORE_SECTION_STORAGE_KEY, moreSection);
      } else {
        sessionStorage.removeItem(SUPERVISOR_MORE_SECTION_STORAGE_KEY);
      }
    } catch (e) {
      // sessionStorage unavailable (e.g. private browsing) — safe to ignore
    }
  }, [activeTab, moreSection]);

  // user role display
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    const loadUser = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) return;

        const userSnap = await getDoc(doc(db, "users", user.uid));

        if (userSnap.exists()) {
          setUserRole(userSnap.data().role || "");
        }
      } catch (err) {
        console.error("SupervisorPortal: error loading user role:", err);
      }
    };

    loadUser();
  }, []);

  // Accounting is Admin-only (its tab button is hidden for everyone else).
  // A remembered "accounting" tab must not reopen it for a non-Admin, e.g.
  // after a different user signs in on the same browser tab.
  useEffect(() => {
    if (userRole && userRole !== "Admin" && activeTab === "accounting") {
      setActiveTab("dashboard");
    }
  }, [userRole, activeTab]);

  // when switching main tabs, clear "more" embeds
  const goTab = (tab) => {
    setActiveTab(tab);
    setMoreSection(null);
  };

  // open a More-menu section (Settings / Operations / Admin)
  const openMore = (section) => {
    setMoreSection(section);
    setActiveTab("dashboard");
    setShowMoreMenu(false);
  };

  // More-menu groups. Every entry renders inside the content box below.
  const MORE_GROUPS = [
    {
      title: "Operations",
      items: [
        { key: "vehicles", label: "Vehicles" },
        { key: "vendors", label: "Vendors" },
        { key: "contractors", label: "Contractors" },
        { key: "supplies", label: "Supplies" },
        { key: "reports", label: "Reports" },
        { key: "notebook", label: "Notebook" },
      ],
    },
    {
      title: "Settings",
      items: [
        { key: "staff", label: "Settings → Staff" },
        { key: "staffsettings", label: "Settings → Staff Settings" },
        { key: "database", label: "Settings → DataBase" },
        { key: "ppddd", label: "Settings → PPD Dropdowns" },
        { key: "invoicedd", label: "Settings → Invoice Dropdowns" },
        { key: "notificationrecipients", label: "Settings → Notifications" },
      ],
    },
    {
      title: "Admin",
      items: [
        { key: "adminportal", label: "Administration (AdminPortal)" },
        { key: "adminaccess", label: "Admin Access" },
      ],
    },
  ];

  // styles
  const topBtn = (path) => {
    const isActive = location.pathname === path;
  
    return {
      padding: "10px 14px",
      borderRadius: 10,
      border: "1px solid #1d4ed8",
      background: isActive ? "#1d4ed8" : "#e0e7ff",
      color: isActive ? "#fff" : "#0f172a",
      fontWeight: 800,
      cursor: "pointer",
      whiteSpace: "nowrap",
    };
  };  

  const tabBtn = (active) => ({
    padding: "10px 14px",
    borderRadius: "10px 10px 0 0",
    border: "1px solid #1d4ed8",
    borderBottom: active ? "1px solid white" : "1px solid #1d4ed8",
    background: active ? "#1d4ed8" : "#93c5fd",
    color: active ? "#fff" : "#0f172a",
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
  });

  const contentWrap = {
    border: "1px solid #1d4ed8",
    borderRadius: "0 10px 10px 10px",
    padding: 16,
    background: "#fff",
    minHeight: 500,
  };

  const moreBtn = {
    padding: "10px 12px",
    borderRadius: "10px 10px 0 0",
    border: "1px solid #1d4ed8",
    background: "#e0e7ff",
    fontWeight: 700,
    cursor: "pointer",
    color: "#000",
    whiteSpace: "nowrap",
  };

  const popupBtn = {
    width: "100%",
    padding: "12px 14px",
    marginBottom: 8,
    borderRadius: 10,
    border: "1px solid #1d4ed8",
    background: "#e0e7ff",
    color: "#0f172a",
    fontWeight: 800,
    cursor: "pointer",
    textAlign: "left",
  };

  return (
    <div style={{ padding: 14 }}>
      {/* TOP NAV */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 10,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        <button onClick={() => navigate("/")} style={topBtn("/")}>
          Home
        </button>
        <button onClick={() => navigate("/JobHistory")} style={topBtn("/JobHistory")}>
          Team Dashboard
        </button>
        <button
          onClick={() => navigate("/SupervisorDashboard")}
          style={topBtn("/SupervisorDashboard")}
        >
          Admin Portal
        </button>
      </div>

      {/* Welcome + Role */}
      <h1 style={{ textAlign: "center", marginBottom: 6 }}>
        Welcome to the Portal
      </h1>
      <p style={{ textAlign: "center", marginTop: 0, marginBottom: 14 }}>
        Role: {userRole || "—"}
      </p>

      {/* TAB HEADER ROW */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 12,
          marginBottom: 0,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            style={tabBtn(!moreSection && activeTab === "dashboard")}
            onClick={() => goTab("dashboard")}
          >
            SupervisorDashboard
          </button>

          <button
            style={tabBtn(!moreSection && activeTab === "properties")}
            onClick={() => goTab("properties")}
          >
            PropertiesClients
          </button>

          <button
            style={tabBtn(!moreSection && activeTab === "management")}
            onClick={() => goTab("management")}
          >
            Management
          </button>

          <button
            style={tabBtn(!moreSection && activeTab === "invoices")}
            onClick={() => goTab("invoices")}
          >
            SendInvoices
          </button>

          <button
            style={tabBtn(!moreSection && activeTab === "approved")}
            onClick={() => goTab("approved")}
          >
            Approved Invoices
          </button>

          <button
            style={tabBtn(!moreSection && activeTab === "licenses")}
            onClick={() => goTab("licenses")}
          >
            LicenseCertificates
          </button>

          <button
            style={tabBtn(!moreSection && activeTab === "timeoff")}
            onClick={() => goTab("timeoff")}
          >
            TimeOffAdmin
          </button>

          <button
            style={tabBtn(!moreSection && activeTab === "contracts")}
            onClick={() => goTab("contracts")}
          >
            Contracts
          </button>

          {userRole === "Admin" && (
            <button
              style={tabBtn(!moreSection && activeTab === "accounting")}
              onClick={() => goTab("accounting")}
            >
              Accounting
            </button>
          )}

          <button
            style={tabBtn(false)}
            onClick={() =>
              window.open(
                "https://prioritypestdefense.com/PPDWDI-ContactSubmission-loveVicki-loveMarilyn-Lovethemallwhocamebeforeus-WeRockonglorious-11798220180-dfgjngir3984hJID843932uw99234R/",
                "_blank",
                "noopener,noreferrer"
              )
            }
          >
            WDI/ Contact Submissions
          </button>
        </div>

        <button onClick={() => setShowMoreMenu(true)} style={moreBtn}>
          ⋯ More
        </button>
      </div>

      {/* CONTENT CONTAINER */}
      <div style={contentWrap}>
        {/* MORE SECTION HAS PRIORITY */}
        {moreSection && (
          <>
            {moreSection === "vehicles" && <Vehicles />}
            {moreSection === "vendors" && <Vendors />}
            {moreSection === "contractors" && <Contractors />}
            {moreSection === "supplies" && <Supplies />}
            {moreSection === "reports" && <Reports />}
            {moreSection === "notebook" && <Notebook />}
            {moreSection === "staff" && <Staff />}
            {moreSection === "staffsettings" && <StaffSettings />}
            {moreSection === "database" && <DataBase />}
            {moreSection === "ppddd" && <ProposalsPPDDropdownsAdmin />}
            {moreSection === "invoicedd" && <InvoiceDropdownsAdmin />}
            {moreSection === "notificationrecipients" && <NotificationRecipients />}
            {moreSection === "adminportal" && <AdminPortal />}
            {moreSection === "adminaccess" && <AdminAccess />}
          </>
        )}

        {/* MAIN TABS (only when no More section is active) */}
        {!moreSection && (
          <>
            {activeTab === "dashboard" && <SupervisorDashboard />}
            {activeTab === "properties" && <PropertiesClients />}
            {activeTab === "management" && <Management />}
            {activeTab === "invoices" && <SendInvoices />}
            {activeTab === "approved" && <ApprovedInvoices />}
            {activeTab === "licenses" && <LicenseCertificates />}
            {activeTab === "timeoff" && <TimeOffAdmin />}
            {activeTab === "contracts" && <Contracts />}
            {activeTab === "accounting" && <Accounting />}
          </>
        )}
      </div>

      {/* MORE MENU POPUP (portal + inline styles so page CSS can't move it) */}
      {showMoreMenu &&
        createPortal(
          <>
            {/* overlay */}
            <div
              onClick={() => setShowMoreMenu(false)}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 999998,
                background: "rgba(0,0,0,0.55)",
              }}
            />

            {/* popup */}
            <div
              role="dialog"
              aria-modal="true"
              aria-label="More sections"
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                zIndex: 999999,
                width: 360,
                maxWidth: "calc(100vw - 24px)",
                maxHeight: "80vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
              {/* scrollable body */}
              <div
                style={{
                  flex: "1 1 auto",
                  minHeight: 0,
                  overflowY: "auto",
                  overscrollBehavior: "contain",
                  WebkitOverflowScrolling: "touch",
                  padding: 12,
                }}
              >
                {MORE_GROUPS.map((group, gi) => (
                  <div key={group.title}>
                    {gi > 0 && (
                      <div style={{ height: 1, background: "#e5e7eb", margin: "10px 0" }} />
                    )}
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 800,
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        margin: "2px 2px 8px",
                      }}
                    >
                      {group.title}
                    </div>
                    {group.items.map((item) => (
                      <button
                        key={item.key}
                        onClick={() => openMore(item.key)}
                        style={{
                          ...popupBtn,
                          ...(moreSection === item.key
                            ? { background: "#1d4ed8", color: "#fff" }
                            : null),
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
              {/* end scrollable body */}

              {/* pinned footer */}
              <div
                style={{
                  flex: "0 0 auto",
                  textAlign: "right",
                  padding: 12,
                  borderTop: "1px solid #e5e7eb",
                  background: "#fff",
                }}
              >
                <button
                  onClick={() => setShowMoreMenu(false)}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: "1px solid #b91c1c",
                    background: "#ef4444",
                    color: "#fff",
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default SupervisorPortal;
